<template>
  <div class="tour-page">
    <!-- Hero Section -->
    <section class="tour-hero-section">
      <div class="hero-background">
        <img src="@/assets/images/natural resources/Kilimanjaro Banner.jpg" alt="Tarangire National Park" class="hero-image" />
        <div class="hero-overlay"></div>
      </div>
      <div class="hero-content">
        <div class="container">
          <h1 class="hero-title" data-aos="fade-up">Heritage Discovery Tour</h1>
          <h2 class="hero-subtitle" data-aos="fade-up" data-aos-delay="100">
            Discover the Tarangire National Park in a Single Day!
          </h2>
          <div class="tour-date-banner" data-aos="zoom-in" data-aos-delay="200">
            <svg class="date-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <span class="date-text">Saturday, 22 August 2026</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Tour Overview -->
    <section class="tour-overview">
      <div class="container">
        <div class="overview-content">
          <div class="overview-text" data-aos="fade-right">
            <h3>An Unforgettable Adventure</h3>
            <p>
              Tarangire National Park is one of Tanzania's most extraordinary safari destinations, renowned for
              its sweeping savannahs, iconic ancient baobab trees, and the life-sustaining Tarangire River that
              defines the park's unique ecosystem.
            </p>
            <p>
              The park is famous for its large elephant herds and exceptional concentration of wildlife, including
              lions, giraffes, zebras, wildebeest, and a rich diversity of bird species. Tarangire provides a truly
              rewarding safari for travelers seeking both adventure and serenity, combining spectacular game viewing
              with a deep sense of connection to nature and Tanzania's vibrant natural heritage.
            </p>
          </div>
          <div class="overview-image" data-aos="fade-left">
            <img src="@/assets/images/natural resources/Antelop.jpg" alt="Wildlife in Tarangire National Park" class="overview-img" />
          </div>
        </div>
      </div>
    </section>

    <!-- Tour Highlights -->
    <section class="tour-highlights">
      <h3 class="section-title" data-aos="fade-up">Tour Highlights</h3>
      <div class="highlights-grid">
        <div class="highlight-card" data-aos="fade-up" data-aos-delay="100">
          <div class="highlight-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9,22 9,12 15,12 15,22"></polyline>
            </svg>
          </div>
          <h4>Elephant Herds</h4>
          <p>Witness Tarangire's iconic large elephant herds — one of the highest elephant concentrations in Tanzania.</p>
        </div>
        <div class="highlight-card" data-aos="fade-up" data-aos-delay="200">
          <div class="highlight-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
          </div>
          <h4>Ancient Baobab Trees</h4>
          <p>Marvel at the iconic ancient baobab trees that define Tarangire's landscape — living monuments thousands of years old.</p>
        </div>
        <div class="highlight-card" data-aos="fade-up" data-aos-delay="300">
          <div class="highlight-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="2"></circle>
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
            </svg>
          </div>
          <h4>Wildlife Diversity</h4>
          <p>Encounter lions, giraffes, zebras, wildebeest and over 550 bird species in their sweeping savannah habitat.</p>
        </div>
        <div class="highlight-card" data-aos="fade-up" data-aos-delay="400">
          <div class="highlight-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"></path>
              <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
            </svg>
          </div>
          <h4>Tarangire River</h4>
          <p>The life-sustaining Tarangire River defines the park's unique ecosystem, attracting wildlife year-round.</p>
        </div>
      </div>
    </section>

    <!-- Tour Details -->
    <section class="tour-details">
      <div class="container">
        <h3 class="section-title" data-aos="fade-up">Tour Information</h3>
        <div class="details-grid">
          <div class="detail-card" data-aos="fade-up" data-aos-delay="100">
            <div class="detail-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12,6 12,12 16,14"></polyline>
              </svg>
            </div>
            <h4>Duration</h4>
            <p>Full Day Experience<br/>(06:00 - 18:00)</p>
          </div>
          <div class="detail-card" data-aos="fade-up" data-aos-delay="200">
            <div class="detail-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
            </div>
            <h4>Location</h4>
            <p>Tarangire National Park<br/>Tanzania</p>
          </div>
          <div class="detail-card" data-aos="fade-up" data-aos-delay="300">
            <div class="detail-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="m22 21-3-3"></path>
                <circle cx="19" cy="16" r="3"></circle>
              </svg>
            </div>
            <h4>Ideal For</h4>
            <p>Adventurers, Photographers<br/>& First-Time Safari Travelers</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Tour Pricing -->
    <section class="tour-pricing">
      <div class="container">
        <h3 class="section-title" data-aos="fade-up">Tour Package Pricing</h3>
        <div class="pricing-grid pricing-grid-3">
          <div class="price-card" data-aos="fade-up" data-aos-delay="100">
            <h4>Tanzanian</h4>
            <div class="price">
              <span class="currency">USD</span>
              <span class="amount">100</span>
            </div>
            <div class="price-note">Tanzania Delegates</div>
          </div>
          <div class="price-card price-card-featured" data-aos="fade-up" data-aos-delay="200">
            <h4>East African</h4>
            <div class="price">
              <span class="currency">USD</span>
              <span class="amount">200</span>
            </div>
            <div class="price-note">East African Delegates</div>
          </div>
          <div class="price-card" data-aos="fade-up" data-aos-delay="300">
            <h4>Foreign</h4>
            <div class="price">
              <span class="currency">USD</span>
              <span class="amount">300</span>
            </div>
            <div class="price-note">International Delegates</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Call to Action -->
    <section class="tour-cta">
      <div class="container">
        <div class="cta-content" data-aos="zoom-in">
          <h3>Ready for Your Adventure?</h3>
          <p>Join us for an unforgettable journey through Tanzania's natural and cultural heritage.</p>
          <button class="btn-register" @click="handleRegistration">
            <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 11H1l6-6 6 6v8a2 2 0 0 1-2 2H9v-8Z"></path>
              <path d="M22 12h-8v8a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-8Z"></path>
            </svg>
            <span>Register for Heritage Tour</span>
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "TourView",
  methods: {
    handleRegistration() {
      this.$router.push('/tour-registration');
    },
  },
};
</script>

<style scoped>
/* Tour Page Styles */
.tour-page {
  min-height: 100vh;
}

/* Hero Section */
.tour-hero-section {
  position: relative;
  height: 70vh;
  min-height: 500px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.7) contrast(1.1);
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.4) 0%,
    rgba(0, 0, 0, 0.2) 50%,
    rgba(74, 222, 128, 0.3) 100%
  );
}

.hero-content {
  position: relative;
  z-index: 10;
  text-align: center;
  color: white;
  width: 100%;
}

.hero-title {
  font-size: 4rem;
  font-weight: 900;
  margin-bottom: 20px;
  text-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
  background: linear-gradient(
    45deg,
    #ffffff,
    var(--color-primary-light),
    #ffffff
  );
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: textShimmer 4s ease-in-out infinite;
}

@keyframes textShimmer {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.hero-subtitle {
  font-size: 1.5rem;
  margin-bottom: 30px;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
}

.tour-date-banner {
  display: inline-flex;
  align-items: center;
  gap: 15px;
  background: rgba(255, 255, 255, 0.95);
  padding: 20px 40px;
  border-radius: var(--radius-lg);
  color: var(--color-primary-dark);
  box-shadow: var(--shadow-xl);
  backdrop-filter: blur(10px);
}

.date-icon {
  width: 32px;
  height: 32px;
  color: var(--color-primary);
}

.date-text {
  font-size: 1.5rem;
  font-weight: bold;
  letter-spacing: 1px;
}

/* Tour Overview */
.tour-overview {
  padding: 80px 0;
  background: var(--color-white);
}

.tour-overview .container {
  padding-left: 60px;
  padding-right: 60px;
}

.overview-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
}

.overview-text h3 {
  font-size: 2.5rem;
  color: var(--color-primary-dark);
  margin-bottom: 30px;
  font-weight: bold;
}

.overview-text p {
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--color-text-primary);
  margin-bottom: 25px;
  padding-right: 20px;
}

.overview-image {
  position: relative;
}

.overview-img {
  width: 100%;
  height: 400px;
  object-fit: cover;
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  transition: all 0.3s ease;
}

.overview-img:hover {
  transform: scale(1.02);
  box-shadow: var(--shadow-2xl);
}

/* Tour Highlights */
.tour-highlights {
  padding: 80px 40px;
  background: var(--color-background-light);
}

.section-title {
  font-size: 2.8rem;
  color: var(--color-primary-dark);
  text-align: center;
  margin-bottom: 50px;
  font-weight: bold;
}

.highlights-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  margin-top: 20px;
}

.highlight-card {
  background: var(--color-white);
  padding: 40px 30px;
  border-radius: var(--radius-xl);
  text-align: center;
  box-shadow: var(--shadow-md);
  transition: all 0.3s ease;
  border-top: 4px solid var(--color-primary);
}

.highlight-card:hover {
  transform: translateY(-8px);
  box-shadow: var(--shadow-xl);
}

.highlight-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 20px;
  padding: 15px;
  background: var(--color-primary-light);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.highlight-icon svg {
  width: 30px;
  height: 30px;
  color: white;
}

.highlight-card h4 {
  font-size: 1.5rem;
  color: var(--color-primary-dark);
  margin-bottom: 15px;
  font-weight: bold;
}

.highlight-card p {
  color: var(--color-text-primary);
  line-height: 1.6;
}

/* Tour Details */
.tour-details {
  padding: 80px 0;
  background: var(--color-white);
}

.tour-details .container {
  padding-left: 60px;
  padding-right: 60px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 50px;
  margin-top: 20px;
}

.detail-card {
  background: var(--color-background-light);
  padding: 40px 30px;
  border-radius: var(--radius-xl);
  text-align: center;
  box-shadow: var(--shadow-md);
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.detail-card:hover {
  border-color: var(--color-primary-light);
  transform: translateY(-5px);
  box-shadow: var(--shadow-lg);
}

.detail-icon {
  width: 60px;
  height: 60px;
  margin: 0 auto 20px;
  padding: 15px;
  background: var(--color-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.detail-icon svg {
  width: 30px;
  height: 30px;
  color: white;
}

.detail-card h4 {
  font-size: 1.4rem;
  color: var(--color-primary-dark);
  margin-bottom: 15px;
  font-weight: bold;
}

.detail-card p {
  color: var(--color-text-primary);
  line-height: 1.6;
}

/* Tour Pricing */
.tour-pricing {
  padding: 80px 0;
  background: var(--color-background-light);
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
}

.pricing-grid-3 {
  grid-template-columns: repeat(3, 1fr);
  max-width: 900px;
}

.price-card-featured {
  border-color: var(--color-primary) !important;
  background: linear-gradient(135deg, rgba(34, 139, 34, 0.05), rgba(74, 222, 128, 0.08)) !important;
  transform: scale(1.04);
  box-shadow: var(--shadow-lg) !important;
}

.price-card {
  background: var(--color-white);
  padding: 40px 30px;
  border-radius: var(--radius-xl);
  text-align: center;
  box-shadow: var(--shadow-md);
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.price-card:hover {
  border-color: var(--color-primary-light);
  transform: translateY(-5px);
  box-shadow: var(--shadow-lg);
}

.price-card h4 {
  font-size: 1.3rem;
  color: var(--color-primary-dark);
  margin-bottom: 20px;
  white-space: nowrap;
  font-weight: bold;
}

.price {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 8px;
  margin-bottom: 10px;
}

.currency {
  font-size: 1.2rem;
  color: var(--color-primary);
  font-weight: bold;
}

.amount {
  font-size: 2.5rem;
  color: var(--color-primary);
  font-weight: bold;
}

.price-note {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  font-style: italic;
}

/* Call to Action */
.tour-cta {
  padding: 80px 0;
  background: linear-gradient(
    135deg,
    var(--color-primary-dark) 0%,
    var(--color-primary) 100%
  );
  color: white;
}

.cta-content {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
}

.cta-content h3 {
  font-size: 2.5rem;
  margin-bottom: 20px;
  font-weight: bold;
}

.cta-content p {
  font-size: 1.2rem;
  margin-bottom: 40px;
  opacity: 0.9;
}

.btn-register {
  background: linear-gradient(
    135deg,
    var(--color-success),
    var(--color-primary-light)
  );
  color: white;
  border: none;
  padding: 20px 50px;
  font-size: 1.3rem;
  font-weight: bold;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 15px;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 0 30px rgba(255, 255, 255, 0.3), 0 10px 25px rgba(0, 0, 0, 0.2);
}

.btn-register:hover {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 0 40px rgba(255, 255, 255, 0.4), 0 15px 35px rgba(0, 0, 0, 0.3);
}

.btn-icon {
  width: 24px;
  height: 24px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.2rem;
  }

  .tour-overview .container,
  .tour-highlights .container,
  .tour-details .container {
    padding-left: 30px;
    padding-right: 30px;
  }

  .overview-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .overview-text h3 {
    font-size: 2rem;
  }

  .highlights-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }

  .details-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .pricing-grid,
  .pricing-grid-3 {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }

  .price-card-featured {
    transform: scale(1);
  }

  .btn-register {
    padding: 18px 40px;
    font-size: 1.1rem;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }

  .tour-date-banner {
    padding: 15px 25px;
  }

  .date-text {
    font-size: 1.2rem;
  }

  .pricing-grid {
    grid-template-columns: 1fr;
  }

  .cta-content h3 {
    font-size: 2rem;
  }
}
</style>